/* Thin wrapper around the Telegram WebApp SDK. The rest of the app must
   import from here (or src/platform) and never touch window.Telegram. */

export function detectTelegram(win: any): boolean {
  const initData = win?.Telegram?.WebApp?.initData;
  return typeof initData === 'string' && initData.length > 0;
}

const w = (typeof window !== 'undefined' ? window : {}) as any;

let activeBackHandler: (() => void) | null = null;

export const IS_TELEGRAM: boolean = detectTelegram(w);

export function getTelegram(): any | null {
  return w?.Telegram?.WebApp ?? null;
}

export function getInitData(): string {
  return getTelegram()?.initData ?? '';
}

export function getTelegramUser(): any | null {
  return getTelegram()?.initDataUnsafe?.user ?? null;
}

export function getStartParam(): string | null {
  return getTelegram()?.initDataUnsafe?.start_param ?? null;
}

export function tgReady(): void {
  try {
    getTelegram()?.ready?.();
  } catch (e) {
    /* no-op outside Telegram */
  }
}

export function tgExpand(): void {
  try {
    getTelegram()?.expand?.();
  } catch (e) {
    /* no-op */
  }
}

export function openLink(url: string): void {
  try {
    getTelegram()?.openLink?.(url);
  } catch (e) {
    /* no-op outside Telegram */
  }
}

export function showBackButton(cb: () => void): void {
  const bb = getTelegram()?.BackButton;
  if (!bb) return;
  try {
    if (activeBackHandler) bb.offClick?.(activeBackHandler);
    bb.onClick?.(cb);
    activeBackHandler = cb;
    bb.show?.();
  } catch (e) {
    /* no-op on older clients */
  }
}

export function hideBackButton(): void {
  const bb = getTelegram()?.BackButton;
  if (!bb) return;
  try {
    if (activeBackHandler) {
      bb.offClick?.(activeBackHandler);
      activeBackHandler = null;
    }
    bb.hide?.();
  } catch (e) {
    /* no-op */
  }
}

export function onThemeChanged(cb: () => void): () => void {
  const tg = getTelegram();
  if (!tg?.onEvent) return () => {};
  tg.onEvent('themeChanged', cb);
  return () => tg.offEvent?.('themeChanged', cb);
}

export function getThemeParams(): Record<string, string> {
  return (getTelegram()?.themeParams ?? {}) as Record<string, string>;
}

/* Writes Telegram's current usable viewport height to a CSS variable
   (--tg-viewport-height) so the layout can size to it instead of 100vh,
   which doesn't match the expanded Mini App viewport on iOS. */
export function applyViewportHeight(): void {
  if (typeof document === 'undefined') return;
  const tg = getTelegram();
  const h = tg?.viewportStableHeight || tg?.viewportHeight;
  if (h) {
    document.documentElement.style.setProperty('--tg-viewport-height', `${h}px`);
  }
}

export function onViewportChanged(cb: () => void): () => void {
  const tg = getTelegram();
  if (!tg?.onEvent) return () => {};
  tg.onEvent('viewportChanged', cb);
  return () => tg.offEvent?.('viewportChanged', cb);
}

export function disableVerticalSwipes(): void {
  try {
    getTelegram()?.disableVerticalSwipes?.();
  } catch (e) {
    /* not available on older clients */
  }
}

export function haptic(type: 'light' | 'medium' | 'heavy' = 'light'): void {
  try {
    getTelegram()?.HapticFeedback?.impactOccurred?.(type);
  } catch (e) {
    /* no-op */
  }
}
